import { useCallback, useEffect, useRef, useState } from 'react';

import { MapContainer, TileLayer, LayersControl, FeatureGroup, GeoJSON } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import * as turf from '@turf/turf';
import L from 'leaflet';
import 'leaflet-easybutton';

import icon from 'leaflet/dist/images/marker-icon.png';

import FileUploadButton from './components/fileUploadButton';
import FullscreenControl from './components/fullScreenControl';
import Geocoder from './components/geocoder';
import ResetMap from './components/ResetMap';
import statusModal from '@forms/components/modals/statusModal';

import { formatCoordStr } from '@utils/helpers';
import { ErrorMessages, GeometryTypes } from '@utils/enums.ts';
import { useGeospatial, useModal, useRequest } from '@stores';
import { useReverseGeocodeMutation } from '@hooks';
import { AorQueries } from '@api/services/aor';

import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet/dist/leaflet.css';
import './map.scss';
import { leafletAttribution } from './helper.jsx';
import { regulatoryBoundaries } from '@pages/PublicNotices/public-notices-map/regulatory-boundaries';
import RegulatoryDistrictLayer from './layers/RegulatoryDistrictLayer.jsx';

//CUSTOM LEAFLET FIXES

const customizePolyHandle = ({ options }) => {
  options.icon.options.iconSize = new L.Point(10, 10);
  options.touchIcon = options.icon;
};

const customizeSimpleShapeHandle = ({ options }) => {
  options.moveIcon.options.iconSize = new L.Point(10, 10);
  options.resizeIcon = options.moveIcon;
  options.touchMoveIcon = options.moveIcon;
  options.touchResizeIcon = options.moveIcon;
};

customizePolyHandle(L.Draw.Polyline.prototype);
customizePolyHandle(L.Edit.PolyVerticesEdit.prototype);
customizeSimpleShapeHandle(L.Edit.SimpleShape.prototype);

let DefaultIcon = L.icon({
  iconUrl: icon,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowAnchor: [4, 62],
  popupAnchor: [-3, -76],
});

L.Marker.prototype.options.icon = DefaultIcon;
L.drawLocal.draw.toolbar.buttons.polyline = 'Add a line feature';
L.drawLocal.draw.toolbar.buttons.marker = 'Add a point feature';
L.drawLocal.draw.toolbar.buttons.polygon = 'Add a polygon feature';

const MyCustomMarker = L.Icon.extend({
  options: {
    iconUrl: icon,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    shadowAnchor: [4, 62],
    popupAnchor: [-3, -76],
  },
});

const styles = {
  div: { display: 'flex', height: '60vh' },
  mapContainer: { flex: 1, width: '100%' },
};

const geoJsonTooltipOptions = {
  direction: 'right',
  permanent: false,
  sticky: true,
  offset: [10, 0],
  opacity: 0.75,
};

const editControlDrawOptions = {
  style: { zIndex: 200 },
  marker: { icon: new MyCustomMarker() },
  polyline: {
    shapeOptions: { weight: 2 },
    showLength: true,
    metric: false,
    feet: false,
  },
  polygon: {
    shapeOptions: { weight: 2 },
    showArea: true,
    showLength: true,
    metric: false,
    feet: false,
  },
  rectangle: false,
  circle: false,
  circlemarker: false,
};

const editControlEditOptions = { remove: false, edit: false };

const projectAreaColor = '#0b6bcb';

const projectLocationStyle = {
  color: projectAreaColor,
  fillColor: projectAreaColor,
  fillOpacity: 0.15,
};

const projectPointToLayer = (feature, latLng) =>
  L.circleMarker(latLng, {
    radius: 7,
    color: projectAreaColor,
    weight: 2,
    fillColor: projectAreaColor,
    fillOpacity: 0.35,
  });

const ResourceMap = ({ onFileSelection, requestFormData, stepNo, resourceData, addFeature }) => {
  const { openSecondaryModal } = useModal();
  const { getActiveSection, isFormReadOnly: isReadOnly, projectBounds } = useRequest();
  const { resourceGeometryData } = useGeospatial();
  const { mutateAsync: reverseGeocodeAsync } = useReverseGeocodeMutation();

  const center = requestFormData?.location?.latitude
    ? [requestFormData.location.latitude, requestFormData.location.longitude]
    : [37.612345, -95.661234];
  const zoom = requestFormData?.location?.latitude ? 10 : 4;

  const projectLocationGeometry = requestFormData?.location?.geometry;

  const projectLocationGeoJson = (() => {
    if (!projectLocationGeometry) return null;

    // Already valid top-level GeoJSON
    if (projectLocationGeometry.type === 'FeatureCollection' || projectLocationGeometry.type === 'Feature') {
      return projectLocationGeometry;
    }

    // Raw geometry -> wrap as Feature
    return {
      type: 'Feature',
      properties: { name: 'Project Location' },
      geometry: projectLocationGeometry,
    };
  })();

  const isSectionActive = getActiveSection().id === stepNo;

  const [showReset, setShowReset] = useState(false);

  const featureGroupRef = useRef();
  const resourceMapRef = useRef();
  const geoJSONRef = useRef();
  const layersControlRef = useRef();
  const hasFitBoundsRef = useRef(false);

  const handleReset = () => {
    const map = resourceMapRef.current;
    setShowReset(false);

    // remove search marker
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker && layer.options.customId === 'search-marker') {
        map.removeLayer(layer); // Remove the marker from the map
      }
    });
    projectBounds && map?.fitBounds(projectBounds);
  };

  const handleObjectCreation = async (e) => {
    let centroid;
    let measurementType;
    let measurementUnit;
    let measurementAmount;

    const { layer } = e;
    const newResourceFeature = layer.toGeoJSON();
    const newGeometry = newResourceFeature?.geometry;

    const isWithinValidDistrict = await AorQueries.isGeometryInValidDistrict(newGeometry);

    if (isWithinValidDistrict) {
      switch (newGeometry?.type?.toUpperCase()) {
        case GeometryTypes.MultiPoint.toUpperCase(): {
          const geoJson = turf.multiPoint(newGeometry?.coordinates);

          centroid = turf.centerOfMass(geoJson);
          break;
        }
        case GeometryTypes.Point.toUpperCase(): {
          const geoJson = turf.point(newGeometry?.coordinates);

          centroid = turf.centroid(geoJson);
          break;
        }
        case GeometryTypes.LineString.toUpperCase(): {
          const geoJson = turf.lineString(newGeometry?.coordinates);
          const miles = turf.length(geoJson, { unit: 'miles' });

          centroid = turf.centerOfMass(geoJson);
          measurementType = 'Linear';
          measurementUnit = 'FOOT';
          measurementAmount = (miles * 5280).toFixed(1);
          break;
        }
        case GeometryTypes.MultiLineString.toUpperCase(): {
          const geoJson = turf.multiLineString(newGeometry?.coordinates);
          const miles = turf.length(geoJson, { unit: 'miles' });

          centroid = turf.centerOfMass(geoJson);
          measurementType = 'Linear';
          measurementUnit = 'FOOT';
          measurementAmount = (miles * 5280).toFixed(1);
          break;
        }
        default: {
          const multiPolygon = Array.isArray(newGeometry?.coordinates[0][0][0])
            ? newGeometry?.coordinates
            : [newGeometry?.coordinates];
          const geoJson = turf.multiPolygon(multiPolygon);
          const area = turf.area(geoJson);

          centroid = turf.centerOfMass(geoJson);
          measurementType = 'Area';
          measurementUnit = 'ACRE';
          measurementAmount = (area / 4046.856422).toFixed(6);
        }
      }

      const centroidCoordinates = centroid.geometry.coordinates;
      const reverseGeocodeData = await reverseGeocodeAsync({
        location: `${centroidCoordinates[0]},${centroidCoordinates[1]}`,
        local: false,
      });
      const state = reverseGeocodeData?.address?.Region ?? null;

      const newProperties = {
        latitude: centroidCoordinates ? formatCoordStr(centroidCoordinates[1]) : null,
        longitude: centroidCoordinates ? formatCoordStr(centroidCoordinates[0]) : null,
        measurementType: measurementType ?? null,
        measurementUnit: measurementUnit ?? null,
        measurementAmount: measurementAmount ?? null,
        state,
      };
      addFeature(newProperties, newGeometry);
    } else if (isWithinValidDistrict === false) {
      openSecondaryModal(statusModal, {
        msg: ErrorMessages.InvalidDistrict,
        status: 'Invalid Geometry Location',
      });
    }

    // Reset map to only render geometries that have been previously accepted.
    featureGroupRef?.current?.clearLayers();
  };

  const getResourceDataBounds = (resourceData) => {
    const validGeoResourceData = {
      ...resourceData,
      features: (resourceData?.features ?? []).filter((f) => f?.geometry && Array.isArray(f.geometry.coordinates)),
    };
    if (validGeoResourceData.features.length === 0) return null;
    const bbox = turf.bbox(validGeoResourceData);
    if (!bbox || bbox.some((n) => !Number.isFinite(n))) return null;
    const bboxPoly = turf.bboxPolygon(bbox);
    return L.latLngBounds([bboxPoly.bbox[1], bboxPoly.bbox[0]], [bboxPoly.bbox[3], bboxPoly.bbox[2]]);
  };

  const getProjectLocationBounds = (projectGeometry) => {
    const bbox = turf.bbox(projectGeometry);
    return L.latLngBounds([
      { lat: bbox[1], lng: bbox[0] },
      { lat: bbox[3], lng: bbox[2] },
    ]);
  };

  //Set attribution
  const InvalidateSize = (map) => {
    map?.attributionControl?.setPrefix(leafletAttribution);
    map?.invalidateSize();
  };

  // Set project bounds only once when the map is first loaded using existing resource data or project bounds if no resource data exists
  const setProjectBounds = useCallback(
    ({ map, resourceData }) => {
      const projectBounds = getProjectLocationBounds(projectLocationGeoJson);
      if (resourceData?.features?.length > 0) {
        // If BOTH project location and aquatic resource data exist, fit bounds to see both
        const resourceBounds = getResourceDataBounds(resourceData);
        if (resourceBounds) {
          const boundsList = [projectBounds, resourceBounds];
          const combined = L.latLngBounds(boundsList[0]);
          // Extend with all remaining bounds
          boundsList.slice(1).forEach((b) => {
            combined.extend(b);
          });
          map?.fitBounds(combined, {
            animate: false,
            padding: [10, 10],
          });
          hasFitBoundsRef.current = true;
        } else {
          hasFitBoundsRef.current = false;
        }
      } else {
        if (projectBounds) {
          const options = { animate: false, padding: [10, 10] };
          if (projectLocationGeometry?.type === GeometryTypes.Point) {
            options.maxZoom = 11;
          }
          map?.fitBounds(projectBounds, options);
          hasFitBoundsRef.current = true;
        } else {
          hasFitBoundsRef.current = false;
        }
      }
    },
    [hasFitBoundsRef, projectLocationGeoJson, projectLocationGeometry?.type]
  );

  //Only invalidate size and set bounds when the section becomes active
  useEffect(() => {
    if (!isSectionActive) return;
    const map = resourceMapRef?.current;
    InvalidateSize(map);
    setProjectBounds({ map, resourceData });
  }, [isSectionActive, projectBounds, resourceData, setProjectBounds]);

  // Fit bounds when resourceGeometryData changes (e.g., after file upload)
  useEffect(() => {
    const map = resourceMapRef?.current;
    if (resourceGeometryData?.features && resourceGeometryData?.features?.length > 0) {
      const bbox = turf.bbox(resourceGeometryData);
      const bboxPoly = turf.bboxPolygon(bbox);
      const bounds = L.latLngBounds([bboxPoly.bbox[1], bboxPoly.bbox[0]], [bboxPoly.bbox[3], bboxPoly.bbox[2]]);
      bboxPoly && map && map.fitBounds(bounds, { animate: false });
    }
  }, [resourceGeometryData]);

  const MyGeoJSONData = () =>
    !resourceData ? null : (
      <GeoJSON
        ref={geoJSONRef}
        data={resourceData}
        onEachFeature={(feature, layer) => {
          layer.bindTooltip(feature.properties.waterName, geoJsonTooltipOptions);
        }}
      />
    );

  return (
    <>
      <div style={styles.div} className='ar-map-container'>
        <MapContainer
          style={styles.mapContainer}
          center={center}
          zoom={zoom}
          ref={resourceMapRef}
          sleepNote={false}
          hoverToWake={false}
          sleepOpacity={1}
          worldCopyJump={true}
        >
          <Geocoder resultZoomLevel={19} setShowReset={setShowReset} />
          <FullscreenControl />
          <LayersControl ref={layersControlRef} position='bottomright'>
            <LayersControl.BaseLayer checked name='OSM Streets'>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='ESRI World Imagery'>
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='USGS World Imagery (zoom limit)'>
              <TileLayer
                attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
                url='https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}'
                maxZoom={16}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='USGS Topographic (zoom limit)'>
              <TileLayer
                attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
                url='https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}'
                maxZoom={16}
              />
            </LayersControl.BaseLayer>
            <LayersControl.Overlay name='USACE Regulatory Districts'>
              <RegulatoryDistrictLayer regulatoryBoundaries={regulatoryBoundaries} />
            </LayersControl.Overlay>
            <LayersControl.Overlay checked name='Project Location'>
              {projectLocationGeoJson ? (
                <GeoJSON
                  data={projectLocationGeoJson}
                  style={projectLocationStyle}
                  pointToLayer={projectPointToLayer}
                  onEachFeature={(feature, layer) => {
                    layer.bindTooltip('Project Location', {
                      direction: 'right',
                      sticky: true,
                      offset: [10, 0],
                      opacity: 0.8,
                    });
                  }}
                />
              ) : null}
            </LayersControl.Overlay>
          </LayersControl>
          <FeatureGroup ref={featureGroupRef}>
            {!isReadOnly && (
              <EditControl
                position='topleft'
                onCreated={handleObjectCreation}
                draw={editControlDrawOptions}
                edit={editControlEditOptions}
              />
            )}
          </FeatureGroup>
          {!isReadOnly && <FileUploadButton handleUpload={onFileSelection} accept='.gdb, .zip, .csv' />}
          <MyGeoJSONData />
          {showReset && <ResetMap handleReset={handleReset} />}
        </MapContainer>
      </div>
    </>
  );
};

export default ResourceMap;
